import { getFlags } from '@/feature-flags';
import type {
  CreateDocumentRequest,
  DocumentCollection,
  DocumentInfo,
  PolicyClaimStatus,
  UserClaim,
  UserPolicy,
  UserPolicyClaims,
} from '@/types';
import axios from 'axios';
import { ROUTE_PATHS, ROUTES } from '../../config';
import { isNullOrUndefined } from './assertions';
import { generateCorrelationIdForBFF } from './common';
import { compareDatesDesc, getHasBeen20DaysSince } from './date';
import { getFrontendBuild, getServiceAPIBaseURL } from './env';

interface FetchUserPolicyClaimsResponse {
  data?: UserPolicyClaims;
  error?: Error;
}

interface FilterSortPolicyClaimsResponse {
  past: UserClaim[];
  active: UserClaim[];
  cancelled: UserClaim[];
}

const CLAIM_STATUS_ORDER_DRAFT = 1;

const CLAIM_STATUS_ORDER_INFO_REQUESTED = 2;

const CLAIM_STATUS_ORDER_OTHER = 3;

// Active claims show 'Draft' before 'Information Requested'.
const CLAIM_STATUS_ORDER: Record<PolicyClaimStatus, number> = {
  // @TODO: BFF needs to change 'draft' to 'Draft'.
  draft: CLAIM_STATUS_ORDER_DRAFT,
  Draft: CLAIM_STATUS_ORDER_DRAFT,
  'Information Requested': CLAIM_STATUS_ORDER_INFO_REQUESTED,
  // All other statuses are ranked to be the same.
  'Information Received': CLAIM_STATUS_ORDER_OTHER,
  'New Claim': CLAIM_STATUS_ORDER_OTHER,
  Finalised: CLAIM_STATUS_ORDER_OTHER,
  Withdrawn: CLAIM_STATUS_ORDER_OTHER,
};

function getIsClaimStatusDraft({ claimStatus }: Partial<UserClaim>) {
  // @TODO: BFF needs to change 'draft' to 'Draft'.
  return claimStatus === 'draft' || claimStatus === 'Draft';
}

function getClaimOrderByStatus({ claimStatus }: UserClaim) {
  // 'CLAIM_STATUS_ORDER_OTHER' handles new and/or unknown statuses.
  return CLAIM_STATUS_ORDER[claimStatus] || CLAIM_STATUS_ORDER_OTHER;
}

function sortClaimsByStatus(a: UserClaim, b: UserClaim) {
  return getClaimOrderByStatus(a) - getClaimOrderByStatus(b);
}

function sortClaimsByDate(a: UserClaim, b: UserClaim) {
  return compareDatesDesc(
    a.updatedDate || a.claimSubmittedDate || a.createdDate,
    b.updatedDate || b.claimSubmittedDate || b.createdDate,
  );
}

function sortClaimsByStatusThenDate(a: UserClaim, b: UserClaim) {
  return sortClaimsByStatus(a, b) || sortClaimsByDate(a, b);
}

let fetchUserPolicyClaimsController: AbortController | null = null;
async function fetchUserPolicyClaims(): Promise<FetchUserPolicyClaimsResponse> {
  const url = `${getServiceAPIBaseURL()}/claims`;

  if (fetchUserPolicyClaimsController) {
    fetchUserPolicyClaimsController.abort();
    fetchUserPolicyClaimsController = null;
  }

  try {
    fetchUserPolicyClaimsController = new AbortController();
    const response = await axios.get<UserPolicyClaims>(url, {
      signal: fetchUserPolicyClaimsController.signal,
      withCredentials: true,
      headers: {
        'x-correlation-id': generateCorrelationIdForBFF(),
        x_cp_fe_build: getFrontendBuild(),
        'content-type': 'application/json',
        'x-csrf': '1',
      },
    });

    const { data } = response;
    if (isNullOrUndefined(data)) {
      return { error: new Error('Error retrieving claims.') };
    }

    return { data };
  } catch (error) {
    console.error(error);
    return { error: error as Error };
  }
}

async function createDraftClaimDocument(
  draftClaimId: string,
  request: CreateDocumentRequest,
): Promise<DocumentInfo> {
  const url = `${getServiceAPIBaseURL()}/draft-claims/${draftClaimId}/documents`;

  const response = await axios.post<DocumentInfo>(url, request, {
    withCredentials: true,
    headers: {
      'x-correlation-id': generateCorrelationIdForBFF(),
      x_cp_fe_build: getFrontendBuild(),
      'content-type': 'application/json',
      'x-csrf': '1',
    },
  });

  return response.data;
}

let draftClaimDocumentsController: AbortController | null = null;
async function getDraftClaimDocuments(draftClaimId: string): Promise<DocumentInfo[]> {
  const url = `${getServiceAPIBaseURL()}/draft-claims/${draftClaimId}/documents`;

  if (draftClaimDocumentsController) {
    draftClaimDocumentsController.abort();
    draftClaimDocumentsController = null;
  }

  draftClaimDocumentsController = new AbortController();
  const response = await axios.get<DocumentCollection>(url, {
    signal: draftClaimDocumentsController.signal,
    withCredentials: true,
    headers: {
      'x-correlation-id': generateCorrelationIdForBFF(),
      x_cp_fe_build: getFrontendBuild(),
      'content-type': 'application/json',
      'x-csrf': '1',
    },
  });

  return response.data.documents;
}

async function deleteDraftClaimDocument(draftClaimId: string, documentId: string): Promise<void> {
  const url = `${getServiceAPIBaseURL()}/draft-claims/${draftClaimId}/documents/${documentId}`;

  await axios.delete(url, {
    withCredentials: true,
    headers: {
      'x-correlation-id': generateCorrelationIdForBFF(),
      x_cp_fe_build: getFrontendBuild(),
      'content-type': 'application/json',
      'x-csrf': '1',
    },
  });
}

async function uploadDocumentToPresignedUrl(
  document: DocumentInfo,
  draftClaimId: string,
  file: File,
): Promise<void> {
  const { uploadUrl, contentType } = document;
  await axios.put(uploadUrl, file, {
    headers: {
      'content-type': contentType,
      'x-amz-meta-draft-claim-id': draftClaimId,
      'x-correlation-id': generateCorrelationIdForBFF(),
    },
  });
}

function findDestinationsForClaim(claim: UserClaim, policies: UserPolicy[] = []) {
  return policies.find(p => p.policyNumber === claim.policyNumber)?.destinations ?? [];
}

function filterSortPolicyClaims(
  upClaims: UserPolicyClaims,
  policies: UserPolicy[] = [],
): FilterSortPolicyClaimsResponse {
  const response: FilterSortPolicyClaimsResponse = { past: [], active: [], cancelled: [] };

  Object.entries(upClaims).forEach(([policyNumber, policyClaims]) => {
    // Using 'forEach' instead of 'map' here, to avoid filtering out claims that were not added.
    policyClaims.forEach(pClaim => {
      // Making a shallow copy for immutability, since we're adding new properties to the claim.
      let claim: UserClaim = { ...pClaim, policyNumber, destinations: [] };
      // 'findDestinationsForClaim' requires a 'policyNumber' for claim.
      const destinations = findDestinationsForClaim(claim, policies);
      if (destinations.length) {
        claim = { ...claim, destinations };
      }

      switch (claim.claimStatus) {
        case 'Withdrawn': {
          response.cancelled.push(claim);
          return;
        }

        case 'Finalised': {
          response.past.push(claim);
          return;
        }

        // @TODO: BFF needs to change 'draft' to 'Draft'.
        case 'draft':
        case 'Draft': {
          /**
           * 'Draft' claims can only visible for up to 20 days (from their 'createdDate').
           * They cannot be resumed in the 'Claims Online' portal beyond that time frame.
           */
          if (!getHasBeen20DaysSince(claim.createdDate)) {
            response.active.push(claim);
          }

          return;
        }

        case 'New Claim':
        case 'Information Requested':
        case 'Information Received': {
          response.active.push(claim);
          return;
        }

        default:
      }
    });
  });

  return {
    past: [...response.past].sort(sortClaimsByDate),
    // Active claims show 'Draft' before 'Information Requested'.
    active: [...response.active].sort(sortClaimsByStatusThenDate),
    cancelled: [...response.cancelled].sort(sortClaimsByDate),
  };
}

function getBFFEndpointForClaim({ policyNumber, claimNo }: UserClaim) {
  return `${getServiceAPIBaseURL()}/userpolicies/${policyNumber}/claims/${claimNo}`;
}

function getPathForResumingDraftClaim(claimNo: number | string) {
  return ROUTE_PATHS[ROUTES.RESUME_CLAIM].replace(':claimNo', `${claimNo}`);
}

function getViewManageLinksForClaim(claim: UserClaim) {
  const { claimNo, claimOrigin } = claim;
  const { canMakeNewClaimInPortal } = getFlags();
  const isClaimInPortal = claimOrigin === 'claimsDomain';

  if (getIsClaimStatusDraft(claim)) {
    return {
      primary: {
        id: 'claim-resume',
        target: canMakeNewClaimInPortal && isClaimInPortal ? '_self' : '_blank',
        label: 'Resume claim',
        href:
          canMakeNewClaimInPortal && isClaimInPortal
            ? getPathForResumingDraftClaim(claimNo)
            : `${getBFFEndpointForClaim(claim)}/redirect`,
      },
    };
  }

  if (!isClaimInPortal) {
    return {
      primary: {
        id: 'claim-upload-document',
        target: '_blank',
        label: 'Upload documents',
        href: `${getBFFEndpointForClaim(claim)}/redirect`,
      },
    };
  }

  return undefined;
}

export {
  createDraftClaimDocument,
  deleteDraftClaimDocument,
  fetchUserPolicyClaims,
  filterSortPolicyClaims,
  getDraftClaimDocuments,
  getIsClaimStatusDraft,
  getPathForResumingDraftClaim,
  getViewManageLinksForClaim,
  uploadDocumentToPresignedUrl,
};
